// Browser Web Push helpers.
//
// Flow:
//   1. Frontend calls `getPushConfig()` → learns whether push is enabled +
//      the VAPID public key. In mock mode (no keys) the "public_key" is null
//      and every /test call returns "mocked" — we still subscribe so the
//      end-to-end wiring is verifiable.
//   2. `enablePush()` requests Notification permission, registers the SW,
//      subscribes, and POSTs the browser subscription to the backend.
//   3. `disablePush()` unsubscribes locally and tells the backend to drop it.
//   4. `sendTestPush()` triggers /api/push/test — the SW fires the toast.

import api from "@/lib/api";

const urlBase64ToUint8Array = (base64) => {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
};

export const pushSupported = () =>
  "serviceWorker" in navigator
  && "PushManager" in window
  && "Notification" in window;

export const getPushConfig = async () => {
  const { data } = await api.get("/push/config");
  return data;  // {enabled, public_key, mocked}
};

const _getRegistration = async () => {
  // The SW is registered by src/index.js; if it hasn't happened yet, do it now.
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) return existing;
  return await navigator.serviceWorker.register("/sw.js");
};

export const getCurrentPushStatus = async () => {
  if (!pushSupported()) return { supported: false, permission: "unsupported", subscribed: false };
  const permission = Notification.permission;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  return { supported: true, permission, subscribed: !!sub };
};

export const enablePush = async () => {
  if (!pushSupported()) throw new Error("Push notifications aren't supported in this browser.");
  const cfg = await getPushConfig();
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("Permission denied.");
  const reg = await _getRegistration();
  // If already subscribed, just return the existing subscription.
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    // In mock mode we still need SOMETHING to subscribe with. The browser
    // will reject the subscribe call without a valid applicationServerKey,
    // so if the server hasn't shipped VAPID keys yet, we can't create a
    // real subscription — surface a warm error and keep the local state
    // "not subscribed".
    if (!cfg.public_key) {
      throw new Error(
        "Notifications aren't configured yet — the app owner needs to add VAPID keys. "
        + "You can still try again once they're set up.",
      );
    }
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(cfg.public_key),
    });
  }
  const payload = sub.toJSON();  // {endpoint, keys:{p256dh, auth}, expirationTime}
  await api.post("/push/subscribe", {
    endpoint: payload.endpoint,
    keys: payload.keys,
    expiration_time: payload.expirationTime || null,
  });
  return { subscribed: true, mocked: cfg.mocked };
};

export const disablePush = async () => {
  if (!pushSupported()) return { subscribed: false };
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg ? await reg.pushManager.getSubscription() : null;
  if (sub) {
    await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
    await sub.unsubscribe();
  }
  return { subscribed: false };
};

export const sendTestPush = async () => {
  const { data } = await api.post("/push/test");
  return data;  // {sent, mocked, cleaned_up_gone, total_subscriptions}
};
